"use client";
import React, { useState, useMemo, useEffect, useRef } from "react";
import grausesAuto from "@/assets/fahrgestellnummer-pruefen/weißes-auto.png";
import SuccessIcon from "@/assets/svgs/Success";
import Image from "next/image";
import ButtonAnimated from "@/components/ButtonAnimated/ButtonAnimated";
import Input from "@/common/Input";
import { CarTopInfo } from "@/common/SelectionChips";
import useBackendUrl from "@/app/helpers/useBackendUrl";
import { useRouter } from "next/navigation";
import FindFin from "@/components/FindFin/FindFin";
import { useAuth } from "@/context/ContextProvider";
import { showToast } from "@/app/helpers/showToast";
import { createVehicleFlow } from "@/app/helpers/vehicleStepFlow";
import trackVehicleCreated from "@/lib/trackVehicleCreated";

// =============================================================
//  TopSection – FIN first, then Brand → KmStand (TOTAL 12 STEPS)
// =============================================================

export const TopSection = ({
  step,
  setStep,

  // Fahrzeugdaten ------------------------------------------------
  selectedBrand,
  setSelectedBrand,
  selectedModel,
  setSelectedModel,
  selectedYear,
  setSelectedYear,
  selectedBauform,
  setSelectedBauform,
  selectedFuel,
  setSelectedFuel,
  selectedVariant,
  setSelectedVariant,
  selectedPs,
  setSelectedPs,
  selectedTransmission,
  setSelectedTransmission,
  selectedDoors,
  setSelectedDoors,
  selectedModelType,
  setSelectedModelType,
  selectedKmStand,
  setSelectedKmStand,

  // FIN ----------------------------------------------------------
  selectedFin,
  setSelectedFin,
  isValidFIN,
  setIsValidFIN,
  setIsFindFinPopupOpen,
}) => {
  const { userData, isAnalyticalChecked } = useAuth();

  const api = useBackendUrl();
  const router = useRouter();

  // 1️⃣  FIN helpers ---------------------------------------------
  const MAX_FIN_LEN = 17;
  // 13-17 Zeichen: 17 = moderne FIN, 13-16 = aeltere Fahrzeuge
  const finRegex = /^[A-HJ-NP-Z0-9]{13,17}$/;
  const sanitizeFIN = (value) =>
    String(value ?? "")
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace(/[^A-HJ-NP-Z0-9]/g, "")
      .slice(0, MAX_FIN_LEN);

  /**
   * FIN input: sanitize, store, validate
   */
  const handleInputChangeFIN = (event) => {
    const sanitized = sanitizeFIN(event.target.value);
    setFinInput(sanitized);
    setSelectedFin(sanitized);
    setIsValidFIN(finRegex.test(sanitized));
  };

  // ----------------------------------------------------------------
  //  Animation-States + generic helpers -----------------------------
  // ----------------------------------------------------------------
  const [animateOut, setAnimateOut] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [isChevronAnimating, setIsChevronAnimating] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  // is-hidden waehrend des Step-Swaps (wie Startseite: swapInvisible) -> verhindert
  // das eine-Frame-Aufblitzen des Subtitles beim Wechsel Subtitle -> Chips.
  const [swapInvisible, setSwapInvisible] = useState(false);

  // ⏳ Submit-Loading nur für Schritt 12
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Roh-Daten – Dropdown-Options  ----------------------------------
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [bauformen, setBauformen] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [variants, setVariants] = useState([]);
  const [psOptions, setPsOptions] = useState([]);
  const [gearboxOptions, setGearboxOptions] = useState([]);
  const [doorOptions, setDoorOptions] = useState([]);
  const [modelTypeOptions, setModelTypeOptions] = useState([]);

  // Input-Strings ---------------------------------------------------
  const [finInput, setFinInput] = useState("");
  const [brandInput, setBrandInput] = useState("");
  const [modelInput, setModelInput] = useState("");
  const [yearInput, setYearInput] = useState("");
  const [bauformInput, setBauformInput] = useState("");
  const [fuelInput, setFuelInput] = useState("");
  const [variantInput, setVariantInput] = useState("");
  const [psInput, setPsInput] = useState("");
  const [transmissionInput, setTransmissionInput] = useState("");
  const [doorsInput, setDoorsInput] = useState("");
  const [modelTypeInput, setModelTypeInput] = useState("");
  const [kmStandInput, setKmStandInput] = useState("");
  const [inputError, setInputError] = useState("");
  // Fehlermeldung beim Step-Wechsel zuruecksetzen (nicht am falschen Feld haengen lassen)
  useEffect(() => {
    setInputError("");
  }, [step]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [filteredItemsStep, setFilteredItemsStep] = useState(0);

  const setFilteredForStep = (items, stepIndex) => {
    setFilteredItems(items);
    setFilteredItemsStep(stepIndex);
  };

  // ----------------------------------------------------------------
  //  3)  Animation helpers ------------------------------------------
  // ----------------------------------------------------------------
  // Ziel-Step + Fade-Status als Refs (wie Startseite): "Fade zuerst" beim Zurueck
  // (getBackStep async) ohne Flackern.
  const nextStepRef = useRef(null);
  const fadeOutDoneRef = useRef(false);
  const handleFadeOut = () => {
    fadeOutDoneRef.current = false;
    setAnimateOut(true);
    setIsAnimating(true);
  };
  // WICHTIG (wie Startseite): waehrend des Swaps wird der Slot unsichtbar
  // geschaltet (swapInvisible). Sonst entfernt setAnimateOut(false) die
  // fade-out-Klasse einen Frame BEVOR CarTopInfo intern auf die Chips
  // umschaltet -> der Subtitle blitzt eine Frame lang bei voller Deckkraft auf.
  const ensureFadeInFor = (target) => {
    setAnimateOut(false);
    setSwapInvisible(true); // 👈 Slot verstecken, solange wir den Step tauschen
    setStep(target);
    nextStepRef.current = target; // Reveal-Marker bis der Gatekeeper feuert
    fadeOutDoneRef.current = false;
  };

  // Gatekeeper (wie Startseite): blendet erst ein, wenn der neue Step committed
  // UND der Slot versteckt ist -> CarTopInfo hat im selben Passive-Effect-Flush
  // bereits auf die Chips umgeschaltet, also kein Aufblitzen des alten Subtitles.
  useEffect(() => {
    if (!swapInvisible) return;
    if (nextStepRef.current == null) return;
    setAnimateIn(true);
    setSwapInvisible(false);
    nextStepRef.current = null;
  }, [swapInvisible, step]);
  const onFadeOutEnd = (e) => {
    if (e.nativeEvent.animationName !== "fadeOut") return;
    // animateOut bleibt true, bis das (ggf. async) Ziel feststeht -> der
    // forwards-Fill der Fade-Out haelt opacity 0, kein Aufblitzen des alten Steps.
    fadeOutDoneRef.current = true;
    if (nextStepRef.current != null) {
      ensureFadeInFor(nextStepRef.current);
    }
  };
  const onFadeInEnd = (e) => {
    if (e.nativeEvent.animationName !== "fadeIn") return;
    setAnimateIn(false);
    setIsAnimating(false);
  };

  // ----------------------------------------------------------------
  //  Autocomplete helpers -------------------------------------------
  // ----------------------------------------------------------------
  const brandAliases = { vw: "Volkswagen" };
  const handleInput = (
    val,
    options,
    setInput,
    setFiltered,
    setSelectedObj,
    currentStep
  ) => {
    const raw = val.trim();
    const lower = raw.toLowerCase();
    setInput(val);

    let filtered;
    if (currentStep === 2 && lower === "vw") {
      filtered = options.filter(
        (o) => o.name.toLowerCase() === brandAliases.vw.toLowerCase()
      );
    } else {
      filtered = options.filter((o) => o.name.toLowerCase().startsWith(lower));
    }

    setFiltered(filtered);

    // Sonderfall kmStand (Schritt 12) – kein Lookup
    if (currentStep === 12) {
      setSelectedKmStand(val.trim());
      return;
    }

    const exactMatch = options.find(
      (o) => o.name.toString().toLowerCase() === val.trim().toLowerCase()
    );
    if (exactMatch) {
      setSelectedObj(exactMatch);
    } else {
      setSelectedObj({ id: "", name: "" });
    }
  };

  const handleSelect = (valObj, setInput, setSelectedObj, setFiltered) => {
    setInput(valObj.name);
    setSelectedObj(valObj);
    setFiltered([]);
  };

  // ----------------------------------------------------------------
  //  Backend-Loaders ------------------------------------------------
  // ----------------------------------------------------------------
  const optionSettersByStep = useMemo(
    () => [
      null, // step 1: FIN
      setBrands,
      setModels,
      setYears,
      setBauformen,
      setFuels,
      setVariants,
      setPsOptions,
      setGearboxOptions,
      setDoorOptions,
      setModelTypeOptions,
    ],
    [
      setBrands,
      setModels,
      setYears,
      setBauformen,
      setFuels,
      setVariants,
      setPsOptions,
      setGearboxOptions,
      setDoorOptions,
      setModelTypeOptions,
    ]
  );

  const allSetInputs = useMemo(
    () => [
      setFinInput,
      setBrandInput,
      setModelInput,
      setYearInput,
      setBauformInput,
      setFuelInput,
      setVariantInput,
      setPsInput,
      setTransmissionInput,
      setDoorsInput,
      setModelTypeInput,
      setKmStandInput,
    ],
    [
      setFinInput,
      setBrandInput,
      setModelInput,
      setYearInput,
      setBauformInput,
      setFuelInput,
      setVariantInput,
      setPsInput,
      setTransmissionInput,
      setDoorsInput,
      setModelTypeInput,
      setKmStandInput,
    ]
  );

  const allSetSelected = useMemo(
    () => [
      null, // FIN handled separately
      setSelectedBrand,
      setSelectedModel,
      setSelectedYear,
      setSelectedBauform,
      setSelectedFuel,
      setSelectedVariant,
      setSelectedPs,
      setSelectedTransmission,
      setSelectedDoors,
      setSelectedModelType,
      setSelectedKmStand,
    ],
    [
      setSelectedBrand,
      setSelectedModel,
      setSelectedYear,
      setSelectedBauform,
      setSelectedFuel,
      setSelectedVariant,
      setSelectedPs,
      setSelectedTransmission,
      setSelectedDoors,
      setSelectedModelType,
      setSelectedKmStand,
    ]
  );

  const vehicleFlow = useMemo(
    () =>
      createVehicleFlow({
        api,
        setOptionsByStep: optionSettersByStep,
        setInputsByStep: allSetInputs,
        setSelectedByStep: allSetSelected,
        setFilteredItems,
        showToast,
        stepOffset: 1,
        alwaysSkipVehicleSteps: [10],
      }),
    [
      api,
      optionSettersByStep,
      allSetInputs,
      allSetSelected,
      setFilteredItems,
    ]
  );

  useEffect(() => {
    vehicleFlow.loadBrands();
  }, [vehicleFlow]);

  useEffect(() => {
    setFilteredForStep([], step);
  }, [step]);

  // Helper ----------------------------------------------------------
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  // ----------------------------------------------------------------
  //  MANUAL-ADD -----------------------------------------------------
  // ----------------------------------------------------------------
  const handleManualAdd = async () => {
    if (!selectedKmStand) {
      showToast("Kein Kilometerstand angegeben", "error");
      return;
    }

    // ⏳ ab hier: Request läuft
    setIsSubmitting(true);
    setIsChevronAnimating(true);

    if (!userData) {
      await wait(3000);
      setIsChevronAnimating(false);
      setIsSubmitting(false);
      setStep(step + 1);
      return;
    }

    const payload = {
      fin: selectedFin,
      email: userData.email,
      kmStand: selectedKmStand,
      marke: selectedBrand.name,
      model: selectedModel.name,
      gearbox: selectedTransmission.name,
      fueltype: selectedFuel.name,
      bodytype: selectedBauform.name,
      baujahr: selectedYear.name,
      doors: selectedDoors.id,
      ps: selectedPs.name,
      variant: selectedVariant?.name ?? "",
      type: selectedModelType?.name ?? "",
      vehicleDataIds: {
        brand: selectedBrand?.id ?? null,
        model: selectedModel?.id ?? null,
        year: selectedYear?.id ?? null,
        body: selectedBauform?.id ?? null,
        fuel: selectedFuel?.id ?? null,
        variant: selectedVariant?.id ?? selectedVariant?.name ?? null,
        kw: selectedPs?.id ?? null,
        gearbox: selectedTransmission?.id ?? null,
        doors: selectedDoors?.id ?? null,
        modelType: selectedModelType?.id ?? null,
      },
      salesdate: "2025-06-01",
    };

    try {
      const [backendRes] = await Promise.all([
        fetch(`${api}/user/add-vehicle-manually`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        wait(2500),
      ]);

      const json = await backendRes.json();

      if (!backendRes.ok) {
        throw new Error(json.error || "Fehler beim Anlegen");
      }

      trackVehicleCreated({ vehicleId: json.id, entrypoint: "fahrgestellnummer-pruefen", sourceFlow: "fin_lookup" });

      const priceCalculationAutomated =
        json?.ab?.priceCalculationAutomated === true ||
        json?.priceCalculationAutomated === true;
      const routeWhenReady = priceCalculationAutomated
        ? `/status/${json.id}`
        : json.vehicleMatchFound
          ? `/preis/${json.id}`
          : `/status/${json.id}`;

      router.prefetch("/");
      setIsChevronAnimating(false);
      setIsSubmitting(false);
      router.push(routeWhenReady);
    } catch (err) {
      setIsChevronAnimating(false);
      setIsSubmitting(false);
      showToast(
        err.message === "Failed to fetch" ? "Netzwerkfehler" : err.message,
        "error"
      );
      console.error(err);
    }
  };

  // ----------------------------------------------------------------
  //  NEXT-HANDLER – alle Steps um +1 verschoben ---------------------
  // ----------------------------------------------------------------
  const handleNext = async () => {
    if (isAnimating) return;

    // Im letzten Schritt, wenn schon am Senden → nichts mehr machen
    if (step === 12 && isSubmitting) return;

    setFilteredForStep([], step);

    if (
      userData &&
      Array.isArray(userData.fins) &&
      userData.fins.length >= 10
    ) {
      showToast(
        "Du hast die maximale Anzahl an Fahrzeugen in deinem Account erreicht. Entferne eines, um dieses Fahrzeug hinzuzufügen.",
        "error"
      );
      return;
    }

    if (step === 1) {
      if (!isValidFIN) {
        setInputError("Bitte gib eine gültige Fahrgestellnummer ein");
        return;
      }
      nextStepRef.current = 2;
      handleFadeOut();
      return;
    }

    if (step === 12) {
      if (!selectedKmStand) {
        return showToast("Kein Kilometerstand angegeben", "error");
      }
      handleManualAdd();
      return;
    }

    // Leeres/ungueltiges Feld -> Fehlermeldung am Input (im Referenz-Stil), nicht weiter.
    // Gueltig, wenn die Auswahl id ODER name hat (Flow nutzt z. B. fuer Variante name).
    const finSelection = [
      selectedBrand,
      selectedModel,
      selectedYear,
      selectedBauform,
      selectedFuel,
      selectedVariant,
      selectedPs,
      selectedTransmission,
      selectedDoors,
      selectedModelType,
    ][step - 2];
    if (!(finSelection && (finSelection.id || finSelection.name))) {
      setInputError(
        {
          2: "Bitte gib eine gültige Automarke ein",
          3: "Bitte gib ein gültiges Modell ein",
          4: "Bitte wähle ein Baujahr",
          5: "Bitte wähle eine Bauform",
          6: "Bitte wähle einen Kraftstoff",
          7: "Bitte wähle eine Variante",
          8: "Bitte wähle den PS-Wert",
          9: "Bitte wähle ein Getriebe",
          10: "Bitte wähle die Türenanzahl",
          11: "Bitte wähle einen Modeltyp",
        }[step] || "Bitte triff eine gültige Auswahl",
      );
      return;
    }

    const target = await vehicleFlow.getNextStep({
      step,
      selections: {
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedBauform,
        selectedFuel,
        selectedVariant,
        selectedPs,
        selectedTransmission,
        selectedDoors,
        selectedModelType,
      },
    });

    if (target == null || target === "STOP") return;

    nextStepRef.current = target;
    handleFadeOut();
  };

  // Zurueck-Navigation. Step 2 (Marke) -> zurueck zum FIN-Schritt (1); ab Step 3
  // uebernimmt getBackStep (raeumt via resetFromStep ab). Fade wie handleNext.
  const handleBack = async () => {
    if (isAnimating) return;
    setFilteredForStep([], step);
    if (step <= 1) return;

    // Fade ZUERST, damit die Auswahl waehrend des Ausblendens geleert wird
    // (Chips bleiben via animateOut-Guard) -> kein Flackern.
    handleFadeOut();
    // Step 3 -> 2 ist der Chip<->Subtitle-Uebergang: Ziel frueh registrieren.
    if (step === 3) nextStepRef.current = 2;

    if (step === 2) {
      // Marke + Folge-Auswahl zuruecksetzen (gleicher Reset-Pfad wie getBackStep),
      // sonst bleibt beim erneuten FIN-Versuch eine veraltete Marke stehen.
      vehicleFlow.resetFromStep(2);
      nextStepRef.current = 1;
      if (fadeOutDoneRef.current) ensureFadeInFor(1);
      return;
    }

    const found = await vehicleFlow.getBackStep({
      step,
      selections: {
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedBauform,
        selectedFuel,
        selectedVariant,
        selectedPs,
        selectedTransmission,
        selectedDoors,
        selectedModelType,
      },
    });
    if (found == null || found === "STOP") {
      setAnimateOut(false);
      setIsAnimating(false);
      fadeOutDoneRef.current = false;
      nextStepRef.current = null;
      return;
    }
    nextStepRef.current = found;
    if (fadeOutDoneRef.current) ensureFadeInFor(found);
  };

  // Step-Labels + Fortschritt + Auswahl-Chips (wie auf der Startseite).
  // FIN-Flow: Step 1 = FIN, Step 2 = Marke, ... Step 12 = Kilometerstand.
  const finHeaders = [
    "",
    "Fahrgestellnummer",
    "Deine Automarke",
    "Dein Modell",
    "Baujahr",
    "Bauform",
    "Kraftstoff",
    "Variante",
    "PS-Wert",
    "Getriebe",
    "Türen",
    "Modeltyp",
    "Kilometerstand",
  ];
  const maxStep = finHeaders.length - 1;
  const percentage =
    step > 0 ? (step === maxStep ? 99 : Math.round((step / maxStep) * 100)) : 0;

  // Chips: nur die abgeschlossenen Brand-Flow-Auswahlen (Marke ab Step 2 ->
  // Anzeige ab Step 3), daher slice(step - 2).
  const selectedChips = useMemo(() => {
    const arr = [];
    if (selectedBrand?.name)
      arr.push({ key: "brand", label: "Marke", value: selectedBrand.name });
    if (selectedModel?.name)
      arr.push({ key: "model", label: "Modell", value: selectedModel.name });
    if (selectedYear?.name)
      arr.push({ key: "year", label: "Baujahr", value: selectedYear.name });
    if (selectedBauform?.name)
      arr.push({ key: "bau", label: "Bauform", value: selectedBauform.name });
    if (selectedFuel?.name)
      arr.push({ key: "fuel", label: "Kraftstoff", value: selectedFuel.name });
    if (selectedVariant?.name)
      arr.push({ key: "var", label: "Variante", value: selectedVariant.name });
    if (selectedPs?.name)
      arr.push({ key: "ps", label: "PS", value: selectedPs.name });
    if (selectedTransmission?.name)
      arr.push({
        key: "gb",
        label: "Getriebe",
        value: selectedTransmission.name,
      });
    if (selectedDoors?.id)
      arr.push({
        key: "doors",
        label: "Türen",
        value: String(selectedDoors.id),
      });
    if (selectedModelType?.name)
      arr.push({ key: "mt", label: "Modeltyp", value: selectedModelType.name });
    return arr.slice(0, Math.max(0, step - 2));
  }, [
    step,
    selectedBrand?.name,
    selectedModel?.name,
    selectedYear?.name,
    selectedBauform?.name,
    selectedFuel?.name,
    selectedVariant?.name,
    selectedPs?.name,
    selectedTransmission?.name,
    selectedDoors?.id,
    selectedModelType?.name,
  ]);

  const allOptions = [
    [], // 1 FIN → no dropdown
    brands,
    models,
    years,
    bauformen,
    fuels,
    variants,
    psOptions,
    gearboxOptions,
    doorOptions,
    modelTypeOptions,
    [], // 12 kmStand → no dropdown
  ];

  const allInputs = [
    finInput,
    brandInput,
    modelInput,
    yearInput,
    bauformInput,
    fuelInput,
    variantInput,
    psInput,
    transmissionInput,
    doorsInput,
    modelTypeInput,
    kmStandInput,
  ];

  // FIN-Anzeige waehrend des Tippens: erst bei 17 Zeichen gruen; 13-16 ist
  // NEUTRAL (koennte eine fertige Alt-FIN sein ODER eine unfertige 17er),
  // sonst haetten Nutzer mit 17er FIN bei 13 ein verfruehtes Gruen und
  // hoeren auf zu tippen. isValidFIN (13-17) bleibt der Submit-Check.
  const finDisplayValid =
    finInput.length === 17 ? true : finInput.length >= 13 ? null : false;

  // valid values – first index = FIN (Anzeige nutzt fuer Step 1 stattdessen
  // finDisplayValid, s.o.)
  const validValues = [
    isValidFIN,
    selectedBrand.id,
    selectedModel.id,
    selectedYear.id,
    selectedBauform.id,
    selectedFuel.id,
    selectedVariant.id,
    selectedPs.id,
    selectedTransmission.id,
    selectedDoors.id,
    selectedModelType.id,
    selectedKmStand,
  ];

  const placeholders = useMemo(
    () => [
      "FIN (z. B. WBAZZZ…)",
      "Audi, VW, BMW…",
      "A3, X5…",
      "2020",
      "Limousine, SUV…",
      "Diesel, Benzin…",
      "30 TDI…",
      "116",
      "Automatik, Schaltgetriebe",
      "Z. B. 3 oder 5",
      "Modelvariante",
      "Kilometerstand",
    ],
    [],
  );

  const preferredSamplesPerBrand = useMemo(
    () => ({ Volkswagen: ["Golf VII", "Polo"] }),
    [],
  );

  const dynamicPlaceholder = useMemo(() => {
    const makeSample = (arr, fallback, brandName) => {
      if (!arr?.length) return fallback;
      const prefs = preferredSamplesPerBrand[brandName];
      if (prefs) {
        const preferred = [];
        prefs.some((pref) => {
          const hit = arr.find(({ name }) =>
            name.toLowerCase().startsWith(pref.toLowerCase())
          );
          if (hit) preferred.push(hit);
          return preferred.length === 2;
        });
        if (preferred.length === 2)
          return `${preferred.map((o) => o.name).join(", ")}…`;
        arr = [...preferred, ...arr.filter((o) => !preferred.includes(o))];
      }
      const alpha = arr.filter((o) => /^[A-Za-zÄÖÜäöü]/.test(o.name));
      const numeric = arr.filter((o) => !/^[A-Za-zÄÖÜäöü]/.test(o.name));
      const ordered = [...alpha, ...numeric];
      const seen = new Set();
      const uniques = ordered.filter(({ name }) => {
        const prefix = name.split(/\s+/)[0].toLowerCase();
        if (seen.has(prefix)) return false;
        seen.add(prefix);
        return true;
      });
      if (!uniques.length) return fallback;
      const pickSpread = (list, n = 2) => {
        if (list.length <= n) return list;
        const step = Math.floor(list.length / n);
        return Array.from(
          { length: n },
          (_, i) => list[Math.min(i * step, list.length - 1)]
        );
      };
      const samples = pickSpread(uniques, 2).map((o) => o.name);
      return `${samples.join(", ")}…`;
    };

    const brandName = selectedBrand?.name;
    switch (step) {
      case 3:
        return makeSample(models, "Modell…", brandName);
      case 7:
        return makeSample(variants, "Variante…", brandName);
      case 11:
        return makeSample(modelTypeOptions, "Modeltyp…", brandName);
      default:
        return placeholders[step - 1];
    }
  }, [
    step,
    models,
    variants,
    modelTypeOptions,
    selectedBrand?.name,
    placeholders,
    preferredSamplesPerBrand,
  ]);

  const inputTypes = [
    "text", // FIN
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "number",
  ];

  // ----------------------------------------------------------------
  //  JSX -------------------------------------------------------------
  // ----------------------------------------------------------------
  const buttonText =
    step === 12 && isSubmitting
      ? "Lädt..."
      : step === 1 || step >= 12
      ? "Auto bewerten"
      : "Weiter";

  const isButtonDisabled = step === 12 && isSubmitting;

  return (
    <section className="hero">
      <div className="section-container">
        <div className={`FNP-top ${step === 13 ? "change-step" : ""}`}>
          <div className="FNP-Top-Animation">
            <Image
              src={grausesAuto}
              alt="Graues Auto"
              className="FNP-gray-car"
              priority
              // Prevent Next from serving a massive image on mobile (was `w=3840`).
              sizes="(max-width: 1040px) 580px, 1100px"
            />
            <div className="FNP-Top-Prüfung">
              <div className="FNP-Top-Prüfung-row">
                <SuccessIcon className="FNP-Success" />
                <span>Gültig</span>
              </div>
              <div className="FNP-Top-Prüfung-row-secondary">
                <span className="FNP-Top-span-main">Kilometer</span>
                <span className="FNP-Top-span-secondary">10.000 KM</span>
              </div>
              <div className="FNP-Top-Prüfung-row-secondary">
                <span className="FNP-Top-span-main">Gültig bis</span>
                <span className="FNP-Top-span-secondary">38 Tage</span>
              </div>
            </div>
          </div>

          <div className="AB-Top">
            <div className="Car-Top-wrapper-AB">
              <h3 className="AB-small-title">Fahrgestellnummer Abfragen</h3>
              {/* Das Kern-Keyword steht als EIN Wort im Markup. Vorher zerlegte
                  ein <br className="mobile" /> es mitten im Wort
                  ("Fahrgestell" / "nummer"): im extrahierten H1-Text der
                  staerksten Seite der Domain kam "Fahrgestellnummer" damit gar
                  nicht vor. Der mobile Umbruch passiert jetzt per CSS
                  (.FNP-Title, hyphens: auto) — optisch derselbe Zeilenfall,
                  nur mit Trennstrich statt zerrissenem Wort. Das Leerzeichen
                  im String vor dem <br /> ist Absicht: Browser und Google werten den
                  Umbruch als Leerraum, einfache HTML-Parser (Bing, KI-Crawler)
                  verwerfen das Tag aber ersatzlos und lesen sonst
                  "Fahrgestellnummeronline". Am Zeilenende ist das Leerzeichen
                  unsichtbar. */}
              <h1 className="AB-Top-title FNP-Title">
                {"Fahrgestellnummer "}
                <br />
                online prüfen
              </h1>
              <CarTopInfo
                items={selectedChips}
                textNode={
                  <p className="AB-Top-Info">
                    Überprüfe die FIN deines Fahrzeugs <br />
                    verfolge seinen Wert und verkaufe
                    <br /> es zum richtigen Zeitpunkt.
                  </p>
                }
                reserveTextSpace
                shouldAnimate={(step <= 2) !== ((nextStepRef.current ?? step) <= 2)}
                animateOut={animateOut}
                animateIn={animateIn}
                hidden={
                  swapInvisible || (isAnimating && !animateOut && !animateIn)
                }
              />

              <div
                className={`Car-Top-Input-Wrapper-main ${
                  animateOut ? "fade-out" : animateIn ? "fade-in" : ""
                } ${swapInvisible ? "is-hidden" : ""}`}
                onAnimationEnd={
                  animateOut ? onFadeOutEnd : animateIn ? onFadeInEnd : null
                }
              >
                <p className="Car-top-FIN desktop">
                  <strong>{finHeaders[step]}</strong>
                  {step > 1 && (
                    <>
                      &nbsp;
                      <span className="step-percentage">
                        ({percentage}% abgeschlossen)
                      </span>
                      <button type="button" className="back-button" onClick={handleBack}>
                        Zurück
                      </button>
                    </>
                  )}
                </p>
                <div className="AB-Input-wrapper">
                  <p className="Car-top-FIN mobile">
                    <strong>{finHeaders[step]}</strong>
                    {step > 1 && (
                      <>
                        &nbsp;
                        <span className="step-percentage">
                          ({percentage}% abgeschlossen)
                        </span>
                        <button type="button" className="back-button" onClick={handleBack}>
                          Zurück
                        </button>
                      </>
                    )}
                  </p>

                  <div className="AB-top-input-values">
                    <Input
                      className={step === 1 ? "FNP-fin-input" : undefined}
                      width="100%"
                      type={inputTypes[step - 1]}
                      value={allInputs[step - 1]}
                      placeholder={dynamicPlaceholder}
                      onFocus={() => {
                        setInputError("");
                        setFilteredForStep(allOptions[step - 1], step);
                      }}
                      valid={
                        // FIN-Step: Tri-State durchreichen (null = neutral bei
                        // 13-16 Zeichen) – !! wuerde null zu false machen
                        step === 1
                          ? finDisplayValid
                          : !!validValues[step - 1]
                      }
                      showValidationWhileFocused={step === 1}
                      onBlur={() => setFilteredForStep([], step)}
                      onChange={(e) => {
                        setInputError("");
                        if (step === 1) {
                          handleInputChangeFIN(e);
                        } else {
                          handleInput(
                            e.target.value,
                            allOptions[step - 1],
                            allSetInputs[step - 1],
                            (allFiltered) =>
                              setFilteredForStep(allFiltered, step),
                            allSetSelected[step - 1],
                            step
                          );
                        }
                      }}
                      inputMode={
                        step === 4 || step === 12 ? "numeric" : undefined
                      }
                      pattern={step === 4 || step === 12 ? "[0-9]*" : undefined}
                      maxLength={step === 1 ? MAX_FIN_LEN : undefined}
                      autoCapitalize={step === 1 ? "characters" : undefined}
                      autoCorrect={step === 1 ? "off" : undefined}
                      spellCheck={step === 1 ? false : undefined}
                      errorMessage={inputError}
                    />
                    {filteredItems.length > 0 &&
                      filteredItemsStep === step &&
                      step !== 1 && (
                      <div className="Input-dropdown-shell">
                        <ul className="Input-dropdown-values">
                        {filteredItems.map((o, i) => (
                          <li
                            key={i}
                            className="Input-dropdown-value"
                            onMouseDown={() =>
                              handleSelect(
                                o,
                                allSetInputs[step - 1],
                                allSetSelected[step - 1],
                                (items) => setFilteredForStep(items, step)
                              )
                            }
                          >
                            {o.name}
                          </li>
                        ))}
                      </ul>
                      </div>
                    )}
                  </div>

                  <ButtonAnimated
                    onClick={handleNext}
                    className="HomePageTopButton desktop"
                    tooltipPosition="top"
                    hideMaxWidth
                    width="100%"
                    text={buttonText}
                    disabled={isButtonDisabled}
                  />
                  <ButtonAnimated
                    onClick={handleNext}
                    className="HomePageTopButton mobile"
                    tooltipPosition="bottom"
                    hideMaxWidth
                    width="100%"
                    text={buttonText}
                    disabled={isButtonDisabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
